/**
 * The journey record. Every screen someone reaches is posted to Miles 360 with
 * their mobile number, so we can see exactly where the funnel loses people.
 *
 * Two shapes worth knowing about:
 *
 * - Miles 360 stores pages as a **set** keyed by phone — no order, no times, and
 *   an identical string posted twice is a no-op. So each page carries its own
 *   ISO timestamp, which makes every entry unique, preserves the order, and lets
 *   time-per-screen be read off the gaps.
 * - We record on *arrival* at a screen, not on leaving it. Entry times are what
 *   make the gaps meaningful, and it is the only way the final screen is ever
 *   recorded at all — nobody advances off the pricing screen.
 *
 * Nothing here may block or break navigation: a tracking failure costs the
 * person nothing.
 */

import { recordPage } from "./miles360";

/** `Game 2 · Myth shredder @ 2026-09-28T12:03:48Z` — seconds are enough to order a flow. */
export function formatPage(name: string, at: Date = new Date()) {
  return `${name} @ ${at.toISOString().replace(/\.\d{3}Z$/, "Z")}`;
}

/* ---------- the retry queue ----------
 * People drop off on patchy mobile connections more than on good ones, so
 * losing failed posts would bias the very numbers we are collecting. Failed
 * entries wait in localStorage and ride along with the next successful post.
 */

const QUEUE_KEY = "caira_view_events_v1";
const QUEUE_MAX = 50;

/** `source_page_url` is optional only because older queued entries lack it. */
export type QueuedPage = { phone: string; page: string; source_page_url?: string };

function readQueue(): QueuedPage[] {
  try {
    if (typeof localStorage === "undefined") return [];
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as QueuedPage[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(events: QueuedPage[]) {
  try {
    if (typeof localStorage === "undefined") return;
    if (!events.length) localStorage.removeItem(QUEUE_KEY);
    /* Keep the most recent: the tail of the funnel is what we came for. */
    else localStorage.setItem(QUEUE_KEY, JSON.stringify(events.slice(-QUEUE_MAX)));
  } catch {
    /* storage full or blocked — tracking is not worth an exception */
  }
}

/** Sends in order, stopping at the first failure. Returns what did not land. */
async function deliver(events: QueuedPage[]): Promise<QueuedPage[]> {
  for (let i = 0; i < events.length; i++) {
    const e = events[i]!;
    const res = await recordPage(e.phone, e.page, e.source_page_url);
    if (!res.ok) return events.slice(i);
  }
  return [];
}

/** Views already recorded this session, so a re-render or double tap posts once. */
const seen = new Set<string>();

/**
 * The mobile we post under, remembered as soon as it is known.
 *
 * The capture screen advances in the same tick that it saves the number, so the
 * store's state has not caught up when that first event fires. Telling the
 * tracker directly closes the gap — without it, the capture screen's own
 * drop-off would go unrecorded for anyone arriving without a prefill.
 */
let identity = "";

export function identifyViewer(phone: string) {
  if (phone) identity = phone;
}

/** Test seam: forget this session and drop anything queued. */
export function resetTracking() {
  seen.clear();
  identity = "";
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(QUEUE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Record one screen. Fire-and-forget: callers must not await it, and it never
 * rejects.
 */
export async function trackView(args: {
  phone: string;
  viewId: string;
  name: string;
  sourcePageUrl: string;
}): Promise<void> {
  try {
    const phone = args.phone || identity;
    if (!phone) return; // no mobile, no post
    if (seen.has(args.viewId)) return;
    seen.add(args.viewId);

    const event: QueuedPage = {
      phone,
      page: formatPage(args.name),
      source_page_url: args.sourcePageUrl,
    };
    /* Anything stranded by an earlier failure goes out ahead of this one. */
    writeQueue(await deliver([...readQueue(), event]));
  } catch (e) {
    console.warn("view tracking failed", e);
  }
}

/** Flush whatever an earlier session could not deliver. Safe to call on load. */
export async function flushQueuedViews(): Promise<void> {
  try {
    const pending = readQueue();
    if (!pending.length) return;
    writeQueue(await deliver(pending));
  } catch (e) {
    console.warn("view tracking flush failed", e);
  }
}
